import { StoreDataService } from './store-data.service';
import { Injectable, OnDestroy, inject } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import type { ISchema } from 'ngx-schema-form';
import { Subscription, filter } from 'rxjs';

/**
 * Service to capture and persist query parameters defined in the user schema
 */
@Injectable({ providedIn: 'root' })
export class QueryParamPersistenceService implements OnDestroy {
	private readonly router = inject(Router);
	private readonly route = inject(ActivatedRoute);
	private readonly storeData = inject(StoreDataService);

	private readonly STORAGE_PREFIX = 'queryParam.';
	private routerSubscription?: Subscription;
	private initialized = false;

	/**
	 * Initialize the service after store data is available
	 */
	init(): void {
		if (this.initialized) {
			return;
		}

		this.initialized = true;
		this.setupRouterListener();
	}

	/**
	 * Get the user schema from store data
	 */
	private get schema(): ISchema {
		return this.storeData.storeData?.storefront?.custom_user_schema as ISchema;
	}

	/**
	 * Get a specific persisted query parameter from localStorage
	 */
	getPersistedQueryParam(paramName: string): string | null {
		return localStorage.getItem(`${this.STORAGE_PREFIX}${paramName}`);
	}

	/**
	 * Listen for router navigation events to capture query parameters
	 */
	private setupRouterListener(): void {
		this.routerSubscription = this.router.events
			.pipe(filter((event) => event instanceof NavigationEnd))
			.subscribe(() => {
				this.captureQueryParams();
			});

		// Capture params on initial load
		this.captureQueryParams();
	}

	/**
	 * Capture query parameters defined in the user schema and store them in localStorage
	 */
	private captureQueryParams(): void {
		// Get parameters to watch from schema
		const paramsToWatch: string[] = [];
		const schema = this.schema;
		if (!schema?.properties) return;

		for (const prop of Object.values(schema.properties)) {
			if (prop?.['x-queryParameter']) {
				paramsToWatch.push(prop['x-queryParameter'] as string);
			}
		}
		// Check if any of these parameters exist in current URL using route snapshot
		const params = this.route.snapshot.queryParamMap;
		for (const paramName of paramsToWatch) {
			if (params.has(paramName)) {
				const value = params.get(paramName);
				if (value) localStorage.setItem(`${this.STORAGE_PREFIX}${paramName}`, value);
			}
		}
	}

	/**
	 * Clear all persisted query parameters
	 */
	clearPersistedQueryParams(): void {
		const keysToRemove: string[] = [];
		// Find all keys with our prefix
		for (let i = 0; i < localStorage.length; i++) {
			const key = localStorage.key(i);
			if (key?.startsWith(this.STORAGE_PREFIX)) {
				keysToRemove.push(key);
			}
		}
		// Remove them
		for (const key of keysToRemove) localStorage.removeItem(key);
	}

	ngOnDestroy(): void {
		this.routerSubscription?.unsubscribe();
	}
}
